/**
 * Export dialog for the attendance and care ratio inspection report (#1501).
 *
 * A download dialog (pick a location and a period, download the report as a PDF or as
 * an Excel workbook), not an entity edit form, so it is built on Dialog rather than
 * BaseEditableForm. Both buttons share the inputs, the validation and the inline error.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react';
import { endOfMonth, startOfMonth } from 'date-fns';
import { AlertCircle, FileDown, FileSpreadsheet, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DateRangePicker, type DateRange } from '@/components/ui/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useChildcares } from '@/hooks/settings/useChildcare';
import {
  useDownloadAttendanceReportPDF,
  useDownloadAttendanceReportXLSX,
} from '@/hooks/useAttendanceReportPdf';
import { errorToastText } from '@/lib/translateBackendError';

export interface AttendanceReportExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The location selected on the dashboard, preselected when it is in the list. */
  defaultChildcareId: string | null;
}

function currentMonthRange(): DateRange {
  const now = new Date();
  return { from: startOfMonth(now), to: endOfMonth(now) };
}

export function AttendanceReportExportDialog({
  open,
  onOpenChange,
  defaultChildcareId,
}: AttendanceReportExportDialogProps) {
  const { i18n } = useLingui();
  const pdfDownload = useDownloadAttendanceReportPDF();
  const xlsxDownload = useDownloadAttendanceReportXLSX();
  const { reset: resetPdf } = pdfDownload;
  const { reset: resetXlsx } = xlsxDownload;
  // One inline error at a time: a new attempt, an input change or closing clears both.
  const resetDownload = useCallback(() => {
    resetPdf();
    resetXlsx();
  }, [resetPdf, resetXlsx]);

  const { data: childcaresData } = useChildcares({ all: true });
  const childcares = useMemo(
    () => (childcaresData?.data ?? []).filter((cc): cc is typeof cc & { id: string } => !!cc.id),
    [childcaresData?.data]
  );

  const [childcareId, setChildcareId] = useState<string | undefined>(undefined);
  const [range, setRange] = useState<DateRange>(currentMonthRange);

  // Every time the dialog opens: the current month, the dashboard's location when it
  // is a known one, else the first location, and no stale error.
  useEffect(() => {
    if (!open) return;
    setRange(currentMonthRange());
    resetDownload();
  }, [open, resetDownload]);

  useEffect(() => {
    if (!open) return;
    const preferred = childcares.find((cc) => cc.id === defaultChildcareId);
    setChildcareId(preferred?.id ?? childcares[0]?.id);
  }, [open, defaultChildcareId, childcares]);

  const handleChildcareChange = useCallback(
    (value: string) => {
      setChildcareId(value);
      resetDownload();
    },
    [resetDownload]
  );

  const handleRangeChange = useCallback(
    (next: DateRange) => {
      setRange(next);
      resetDownload();
    },
    [resetDownload]
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) resetDownload();
      onOpenChange(next);
    },
    [onOpenChange, resetDownload]
  );

  const { mutate: mutatePdf } = pdfDownload;
  const { mutate: mutateXlsx } = xlsxDownload;

  const handleDownloadPdf = useCallback(() => {
    if (!childcareId || !range.from || !range.to) return;
    resetXlsx();
    mutatePdf({ childcareId, from: range.from, to: range.to }, { onSuccess: () => onOpenChange(false) });
  }, [childcareId, range.from, range.to, mutatePdf, resetXlsx, onOpenChange]);

  const handleDownloadXlsx = useCallback(() => {
    if (!childcareId || !range.from || !range.to) return;
    resetPdf();
    mutateXlsx({ childcareId, from: range.from, to: range.to }, { onSuccess: () => onOpenChange(false) });
  }, [childcareId, range.from, range.to, mutateXlsx, resetPdf, onOpenChange]);

  const isPending = pdfDownload.isPending || xlsxDownload.isPending;
  const canDownload = !!childcareId && !!range.from && !!range.to && !isPending;
  const error = pdfDownload.error ?? xlsxDownload.error;
  // A known backend code reads translated, a 4xx reason as sent, anything else as the
  // HTTP fallback; a request that never got an answer reads as offline.
  const errorText = error
    ? errorToastText(
        error,
        i18n,
        typeof error.status === 'number'
          ? i18n._({
              id: 'coverage.attendanceReport.httpError',
              message: 'Download failed (HTTP {status})',
              values: { status: error.status },
            })
          : i18n._({ id: 'common.queryError.fallback', message: 'Check your connection and try again.' })
      )
    : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'coverage.attendanceReport.title', message: 'Attendance and care report' })}
          </DialogTitle>
          <DialogDescription>
            {i18n._({
              id: 'coverage.attendanceReport.description',
              message:
                'One PDF or Excel file per location and period for the childcare supervisory authority. The document is always in German.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="attendance-report-location">
              {i18n._({ id: 'coverage.attendanceReport.location', message: 'Location' })}
            </Label>
            {childcares.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {i18n._({ id: 'coverage.attendanceReport.noLocations', message: 'No locations configured' })}
              </p>
            ) : (
              <Select value={childcareId} onValueChange={handleChildcareChange}>
                <SelectTrigger id="attendance-report-location" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {childcares.map((cc) => (
                    <SelectItem key={cc.id} value={cc.id}>
                      {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">
              {i18n._({ id: 'coverage.attendanceReport.period', message: 'Period' })}
            </legend>
            <DateRangePicker from={range.from} to={range.to} onSelect={handleRangeChange} />
            <p className="text-sm text-muted-foreground">
              {i18n._({ id: 'coverage.attendanceReport.limit', message: 'At most 92 days per report.' })}
            </p>
          </fieldset>

          {error && (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>
                {i18n._({
                  id: 'coverage.attendanceReport.errorTitle',
                  message: 'The report could not be created',
                })}
              </AlertTitle>
              <AlertDescription>{errorText}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleDownloadXlsx} disabled={!canDownload}>
            {xlsxDownload.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FileSpreadsheet className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {xlsxDownload.isPending
              ? i18n._({ id: 'coverage.attendanceReport.downloadingExcel', message: 'Generating Excel file' })
              : i18n._({ id: 'coverage.attendanceReport.downloadExcel', message: 'Download Excel' })}
          </Button>
          <Button onClick={handleDownloadPdf} disabled={!canDownload}>
            {pdfDownload.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <FileDown className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {pdfDownload.isPending
              ? i18n._({ id: 'coverage.attendanceReport.downloading', message: 'Generating PDF' })
              : i18n._({ id: 'coverage.attendanceReport.download', message: 'Download PDF' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
